import { PageIcon } from "@/components/PageIcon";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { colourClasses } from "@/lib/colours";
import { statusMeta } from "@/lib/shiftStatus";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarRange, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Ban, Wrench, Plus, KeyRound, LayoutTemplate, Route, Package, Star, Users } from "lucide-react";
import OnShiftBoard from "@/components/OnShiftBoard";
import SchedulerKey from "@/components/scheduler/SchedulerKey";
import ShiftForm from "@/components/scheduler/ShiftForm";
import ShiftSummaryPanel from "@/components/scheduler/ShiftSummaryPanel";
import DaySummaryPanel from "@/components/scheduler/DaySummaryPanel";
import ShiftPatternsManager from "@/components/scheduler/ShiftPatternsManager";
import RunPlanner from "@/components/scheduler/RunPlanner";

function initials(name) {
  if (!name) return "?";
  return name.split(" ").slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}
const dayLabel = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return { dow: d.toLocaleString("en-GB", { weekday: "short" }), dm: d.toLocaleString("en-GB", { day: "numeric", month: "short" }) };
};

export default function Scheduler() {
  const navigate = useNavigate();
  const [view, setView] = useState("week");
  const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKey, setShowKey] = useState(false);
  const [showPatterns, setShowPatterns] = useState(false);
  const [workTypes, setWorkTypes] = useState([]);
  const [patterns, setPatterns] = useState([]);
  const [patternDrag, setPatternDrag] = useState(null);
  useEffect(() => { api.get("/scheduler/meta").then((r) => { setWorkTypes(r.data.work_types || []); setPatterns(r.data.shift_patterns || []); }).catch(() => {}); }, []);

  const [form, setForm] = useState(null);        // {shift?, initial?}
  const [summaryId, setSummaryId] = useState(null);
  const [daySummary, setDaySummary] = useState(null); // {vehicle, date}
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null); // `${vid}|${date}`
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [runOpen, setRunOpen] = useState(false);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const cardClick = (s) => { if (selectMode) toggleSel(s.shift_id); else openShift(s.shift_id); };

  const load = useCallback(() => {
    setLoading(true);
    let apiStart = start;
    if (view === "month" && start) {
      const dt = new Date(start + "T00:00:00");
      const first = new Date(dt.getFullYear(), dt.getMonth(), 1);
      first.setDate(first.getDate() - ((first.getDay() + 6) % 7));
      apiStart = first.toISOString().slice(0, 10);
    }
    api.get("/scheduler", { params: { view, start: apiStart || undefined } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [view, start]);
  useEffect(() => { load(); }, [load]);

  const shift = (dir) => {
    const base = new Date((start || data?.start) + "T00:00:00");
    if (view === "month") base.setMonth(base.getMonth() + dir, 1);
    else base.setDate(base.getDate() + dir * 7);
    setStart(base.toISOString().slice(0, 10));
  };
  const today = () => setStart(new Date().toISOString().slice(0, 10));
  const canWrite = data?.can_write;

  const cellShifts = (vid, date) => (data?.shifts || []).filter((s) => s.vehicle_legacy_id === vid && s.date === date);
  const isUnavail = (vid, date) => (data?.unavailability || []).some((u) => u.vehicle_legacy_id === vid && u.date === date);
  const unavailFor = (vid, date) => (data?.unavailability || []).find((u) => u.vehicle_legacy_id === vid && u.date === date);
  const isMaint = (u) => /servic|mainten|mot/i.test(u?.reason || "");
  const durMins = (s, e) => { if (!s || !e) return 120; const [sh, sm] = s.split(":").map(Number); const [eh, em] = e.split(":").map(Number); return Math.max(15, (eh * 60 + em) - (sh * 60 + sm)); };
  const dayShiftsFor = (date) => (data?.shifts || []).filter((s) => s.date === date).sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const todayIso = new Date().toISOString().slice(0, 10);
  const startMonth = new Date((start || todayIso) + "T00:00:00").getMonth();
  const gridCols = data ? `200px repeat(${data.dates.length}, minmax(150px, 1fr))` : "200px";

  const openShift = (id) => { setSummaryId(id); setDaySummary(null); setForm(null); };
  const openDay = (vehicle, date) => { setDaySummary({ vehicle, date }); setSummaryId(null); };

  const cellClick = (veh, d) => {
    const cells = cellShifts(veh.legacy_id, d);
    if (cells.length > 0) openDay(veh.legacy_id, d);
    else if (canWrite) setForm({ initial: { vehicle_legacy_id: veh.legacy_id, date: d } });
  };

  const moveShift = async (veh, d) => {
    setDragOver(null);
    const id = dragId; setDragId(null);
    if (!id) return;
    const s = (data?.shifts || []).find((x) => x.shift_id === id);
    if (!s || (s.vehicle_legacy_id === veh.legacy_id && s.date === d)) return;
    try { await api.put(`/scheduler/shifts/${id}`, { vehicle_legacy_id: veh.legacy_id, date: d }); toast.success("Shift moved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not move shift"); }
  };

  const dropPattern = async (veh, d) => {
    const p = patternDrag; setPatternDrag(null); setDragOver(null);
    if (!p) return;
    try {
      await api.post("/scheduler/shifts", {
        vehicle_legacy_id: veh.legacy_id, date: d,
        work_type: p.work_type || undefined,
        start_time: p.start_time, end_time: p.end_time,
        duration_mins: durMins(p.start_time, p.end_time),
        shift_pattern: p.name,
      });
      toast.success(`Shift created from "${p.name}"`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create shift"); }
  };

  return (
    <div className="px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8" data-testid="page-scheduler">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <PageIcon />
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Scheduler</h1>
            <p className="text-sm text-slate-500 mt-0.5">Assign shifts to assets &amp; crews · click a shift for its summary</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Button variant="outline" className="h-9 gap-1.5" onClick={() => setShowKey((v) => !v)} data-testid="scheduler-key-btn"><KeyRound className="w-4 h-4" />Key</Button>
            {showKey && <SchedulerKey onClose={() => setShowKey(false)} />}
          </div>
          <Button variant="outline" className="h-9 gap-1.5" onClick={() => setShowPatterns(true)} data-testid="scheduler-patterns-btn"><LayoutTemplate className="w-4 h-4" />Patterns</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(-1)} data-testid="scheduler-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" className="h-9" onClick={today} data-testid="scheduler-today">Today</Button>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => shift(1)} data-testid="scheduler-next"><ChevronRight className="w-4 h-4" /></Button>
          <Select value={view} onValueChange={(v) => { setView(v); }}>
            <SelectTrigger className="h-9 w-28" data-testid="scheduler-view"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
            </SelectContent>
          </Select>
          {canWrite && <Button variant={selectMode ? "default" : "outline"} className={`h-9 gap-1.5 ${selectMode ? "bg-indigo-600 text-white" : ""}`} onClick={() => { setSelectMode((v) => !v); setSelected([]); }} data-testid="scheduler-select-mode"><Route className="w-4 h-4" />{selectMode ? "Selecting…" : "Create run"}</Button>}
          {canWrite && <Button className="h-9 gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setForm({})} data-testid="scheduler-new-shift"><Plus className="w-4 h-4" />New Shift</Button>}
        </div>
      </div>

      <OnShiftBoard />
      {(data?.runs_recalc || []).length > 0 && (
        <div className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-2.5" data-testid="scheduler-recalc-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <span className="font-bold">{data.runs_recalc.length} run{data.runs_recalc.length === 1 ? "" : "s"} need recalculating.</span>{" "}
            {data.runs_recalc.map((r, i) => (
              <React.Fragment key={r.run_id}>
                <button onClick={() => navigate(`/runs/${r.run_id}`)} className="underline font-medium hover:text-amber-900" data-testid={`scheduler-recalc-${r.run_id}`}>{r.name}</button>
                <span className="text-amber-600"> ({r.changed_count} changed){i < data.runs_recalc.length - 1 ? ", " : ""}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5 text-rose-500" /> Conflict</span>
        <span className="flex items-center gap-1.5"><Ban className="w-3.5 h-3.5 text-slate-500" /> Unavailable</span>
        <span className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5 text-amber-600" /> Maintenance</span>
        {canWrite && <span>· click a cell to schedule · drag a shift to move it</span>}
      </div>

      {canWrite && view === "week" && patterns.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="pattern-palette">
          <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-1"><LayoutTemplate className="w-3.5 h-3.5" /> Drag a pattern onto a cell</span>
          {patterns.map((p) => (
            <div key={p.id} draggable
              onDragStart={(e) => { setPatternDrag(p); e.dataTransfer.effectAllowed = "copy"; }}
              onDragEnd={() => { setPatternDrag(null); setDragOver(null); }}
              className="cursor-grab active:cursor-grabbing rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-100 transition-colors flex items-center gap-1.5"
              data-testid={`pattern-chip-${p.id}`}>
              <LayoutTemplate className="w-3 h-3" /> {p.name}
              <span className="text-indigo-400">{p.start_time}–{p.end_time}</span>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="scheduler-grid">
        {loading || !data ? (
          <div className="p-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : view === "month" ? (
          <div className="p-2 sm:p-3" data-testid="scheduler-month">
            <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-lg overflow-hidden border border-slate-200">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="bg-slate-50 px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">{d}</div>
              ))}
              {data.dates.map((d) => {
                const dt = new Date(d + "T00:00:00");
                const inMonth = dt.getMonth() === startMonth;
                const isToday = d === todayIso;
                const dayShifts = dayShiftsFor(d);
                return (
                  <div key={d} onClick={() => { if (!selectMode) { setStart(d); setView("week"); } }}
                    className={`min-h-[118px] p-1.5 flex flex-col gap-1 cursor-pointer transition-colors ${inMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/70 hover:bg-slate-100"}`}
                    data-testid={`scheduler-month-cell-${d}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold flex items-center justify-center ${isToday ? "bg-indigo-600 text-white w-5 h-5 rounded-full" : inMonth ? "text-slate-700" : "text-slate-400"}`}>{dt.getDate()}</span>
                      {dayShifts.length > 0 && <span className="text-[10px] text-slate-400 font-medium">{dayShifts.length}</span>}
                    </div>
                    <div className="flex flex-col gap-1">
                      {dayShifts.slice(0, 3).map((s) => {
                        const meta = statusMeta(s.shift_status);
                        const c = colourClasses(meta.colour);
                        const conflict = s.conflicts && s.conflicts.length > 0;
                        return (
                          <button key={s.shift_id} onClick={(e) => { e.stopPropagation(); cardClick(s); }} data-testid={`scheduler-month-shift-${s.shift_id}`}
                            className={`text-left rounded px-1.5 py-1 border ${c.bg} ${c.border} hover:shadow-sm transition-shadow ${conflict ? "ring-1 ring-rose-400" : ""} ${selectMode && selected.includes(s.shift_id) ? "ring-2 ring-indigo-500" : ""}`}>
                            <div className={`flex items-center gap-1 text-[10px] font-bold ${c.text}`}>
                              <span className="truncate">{s.start_time}{s.end_time ? `–${s.end_time}` : ""}</span>
                              {conflict && <AlertTriangle className="w-2.5 h-2.5 text-rose-500 shrink-0" />}
                            </div>
                            <div className="text-[10px] font-medium text-slate-700 truncate leading-tight">{s.task_title || (s.task_legacy_id ? `Job #${s.task_legacy_id}` : s.work_type || "Shift")}</div>
                          </button>
                        );
                      })}
                      {dayShifts.length > 3 && (
                        <button onClick={(e) => { e.stopPropagation(); setStart(d); setView("week"); }} data-testid={`scheduler-month-more-${d}`}
                          className="text-[10px] text-indigo-600 font-semibold pl-0.5 text-left hover:underline">+{dayShifts.length - 3} more</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-400 mt-2 px-1">Click any day to open its week and edit or schedule shifts.</p>
          </div>
        ) : (
          <div style={{ minWidth: "max-content" }}>
            <div style={{ display: "grid", gridTemplateColumns: gridCols }} className="border-b border-slate-200 bg-slate-50 sticky top-0">
              <div className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500">Vehicle</div>
              {data.dates.map((d) => {
                const { dow, dm } = dayLabel(d);
                const isToday = d === new Date().toISOString().slice(0, 10);
                return <div key={d} className={`px-2 py-3 text-center border-l border-slate-100 ${isToday ? "bg-indigo-50" : ""}`}>
                  <div className="text-[11px] font-semibold text-slate-500 uppercase">{dow}</div>
                  <div className={`text-xs ${isToday ? "text-indigo-700 font-bold" : "text-slate-700"}`}>{dm}</div>
                </div>;
              })}
            </div>
            {data.vehicles.map((veh) => (
              <div key={veh.legacy_id} style={{ display: "grid", gridTemplateColumns: gridCols }} className="border-b border-slate-100">
                <button onClick={() => navigate(`/plant-transport/${veh.legacy_id}`)} data-testid={`scheduler-vehicle-${veh.legacy_id}`}
                  className="px-4 py-3 text-left hover:bg-slate-50 transition-colors">
                  <div className="text-sm font-semibold text-indigo-700 truncate">{veh.title}</div>
                  <div className="text-xs text-slate-400 mono">{veh.registration || "—"}</div>
                </button>
                {data.dates.map((d) => {
                  const cells = cellShifts(veh.legacy_id, d);
                  const urec = unavailFor(veh.legacy_id, d);
                  const unavail = !!urec;
                  const maint = isMaint(urec);
                  const isOver = dragOver === `${veh.legacy_id}|${d}`;
                  return (
                    <div key={d} onClick={() => cellClick(veh, d)}
                      onDragOver={(e) => { if (canWrite && (dragId || patternDrag)) { e.preventDefault(); setDragOver(`${veh.legacy_id}|${d}`); } }}
                      onDragLeave={() => setDragOver((o) => (o === `${veh.legacy_id}|${d}` ? null : o))}
                      onDrop={(e) => { e.preventDefault(); if (patternDrag) dropPattern(veh, d); else moveShift(veh, d); }}
                      className={`px-1.5 py-1.5 border-l border-slate-100 flex flex-wrap gap-1.5 content-start min-h-[40px] cursor-pointer hover:bg-slate-50/60 ${unavail ? (maint ? "bg-amber-50" : "bg-slate-100") : ""} ${isOver ? "bg-indigo-50 ring-2 ring-indigo-300 ring-inset" : ""}`}
                      data-testid={`scheduler-cell-${veh.legacy_id}-${d}`}>
                      {unavail && (maint
                        ? <div className="w-full text-[10px] text-amber-700 flex items-center gap-1" data-testid={`scheduler-maint-${veh.legacy_id}-${d}`}><Wrench className="w-3 h-3" /> Maintenance</div>
                        : <div className="w-full text-[10px] text-slate-500 flex items-center gap-1"><Ban className="w-3 h-3" /> Unavailable</div>)}
                      {cells.map((s) => {
                        const meta = statusMeta(s.shift_status);
                        const c = colourClasses(meta.colour);
                        const Icon = meta.Icon;
                        const conflict = s.conflicts && s.conflicts.length > 0;
                        return (
                          <button key={s.shift_id} draggable={canWrite}
                            onDragStart={(e) => { e.stopPropagation(); setDragId(s.shift_id); e.dataTransfer.effectAllowed = "move"; }}
                            onDragEnd={() => { setDragId(null); setDragOver(null); }}
                            onClick={(e) => { e.stopPropagation(); cardClick(s); }} data-testid={`scheduler-card-${s.shift_id}`}
                            className={`group relative flex-1 basis-[92px] min-w-[92px] text-left rounded-lg border p-1.5 hover:shadow-lg hover:z-20 hover:-translate-y-0.5 transition-all duration-200 ease-out ${c.bg} ${c.border} ${conflict ? "ring-2 ring-rose-400" : ""} ${selectMode && selected.includes(s.shift_id) ? "ring-2 ring-indigo-500" : ""} ${dragId === s.shift_id ? "opacity-40" : ""} ${canWrite ? "cursor-grab active:cursor-grabbing" : ""}`}>
                            {selectMode && <span className={`absolute top-1 right-1 w-4 h-4 rounded border flex items-center justify-center text-[9px] font-bold ${selected.includes(s.shift_id) ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300"}`} data-testid={`run-select-${s.shift_id}`}>{selected.includes(s.shift_id) ? "✓" : ""}</span>}
                            <div className="flex items-center justify-between gap-1">
                              <span className={`flex items-center gap-1 text-[11px] font-bold ${c.text}`}><Icon className="w-3 h-3" />{s.start_time}{s.end_time ? `–${s.end_time}` : ""}</span>
                              {conflict && <AlertTriangle className="w-3 h-3 text-rose-500 shrink-0" title={s.conflicts.join(", ")} />}
                            </div>
                            <div className="text-[11px] font-semibold text-slate-800 leading-tight line-clamp-1" title={s.task_title || ""}>{s.task_title || (s.task_legacy_id ? `Job #${s.task_legacy_id}` : s.work_type || "Shift")}</div>
                            <div className="text-[10px] text-slate-500 truncate">{s.task_legacy_id ? `#${s.task_legacy_id} · ` : ""}{s.venue_postcode || s.venue_city || ""}</div>
                            {s.outstanding_requests > 0 && (
                              <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-300 px-1.5 py-0.5 text-[9px] font-bold text-amber-800"
                                title={`${s.outstanding_requests} parts outstanding`} data-testid={`scheduler-parts-badge-${s.shift_id}`}>
                                <Package className="w-2.5 h-2.5" /> {s.outstanding_requests} parts
                              </div>
                            )}
                            {s.crew_activity && (
                              <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`crew-activity-${s.shift_id}`}>
                                {s.crew_activity.lead_name && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 text-[9px] font-bold text-emerald-800" title={`Lead: ${s.crew_activity.lead_name}`} data-testid={`crew-lead-${s.shift_id}`}>
                                    <Star className="w-2 h-2" /> {(s.crew_activity.lead_name || "").split(" ")[0]}
                                  </span>
                                )}
                                {s.crew_activity.total > 1 && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 border border-slate-300 px-1.5 py-0.5 text-[9px] font-semibold text-slate-600" title="Crew on site"><Users className="w-2 h-2" />{s.crew_activity.summary_label}</span>
                                )}
                                {s.crew_activity.exception && (
                                  <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-100 border border-rose-300 px-1.5 py-0.5 text-[9px] font-bold text-rose-700" title="A crew member has not arrived while the job is in progress" data-testid={`crew-exception-${s.shift_id}`}><AlertTriangle className="w-2 h-2" />crew</span>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-1 mt-1">
                              {(s.staff || []).slice(0, 3).map((st) => (
                                <span key={st} title={st} className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[8px] font-bold flex items-center justify-center">{initials(st)}</span>
                              ))}
                              <span className="text-[10px] text-slate-500 truncate">{(s.staff || [])[0]}</span>
                            </div>
                            <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out">
                              <div className="overflow-hidden">
                                <div className="mt-1.5 pt-1.5 border-t border-black/5 space-y-0.5 text-[10px] text-slate-600">
                                  <div><span className="text-slate-400">Work Type:</span> {s.work_type || "—"}</div>
                                  {(s.staff || []).length > 0 && <div><span className="text-slate-400">Crew:</span> {(s.staff || []).join(", ")}</div>}
                                  {s.crew_activity && (
                                    <>
                                      <div><span className="text-slate-400">Job status:</span> {(s.crew_activity.job_status || "").replace(/_/g, " ")}</div>
                                      {s.crew_activity.members.map((m) => (
                                        <div key={m.id} className="flex justify-between gap-2">
                                          <span className="truncate">{m.name}{m.is_lead ? " · Lead" : ""}</span>
                                          <span className={m.activity === "on_site" || m.activity === "finished" ? "text-emerald-600" : m.activity === "en_route" ? "text-amber-600" : "text-slate-400"}>{m.activity_label}</span>
                                        </div>
                                      ))}
                                    </>
                                  )}
                                  {s.location && <div className="truncate"><span className="text-slate-400">Location:</span> {s.location}</div>}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {form && <ShiftForm shift={form.shift} initial={form.initial} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {showPatterns && <ShiftPatternsManager workTypes={workTypes} onClose={() => setShowPatterns(false)} />}
      {summaryId && <ShiftSummaryPanel shiftId={summaryId} onClose={() => setSummaryId(null)} onReload={load}
        onEdit={(s) => { setSummaryId(null); setForm({ shift: s }); }}
        onOpenDay={(vehicle, date) => { setSummaryId(null); openDay(vehicle, date); }} />}
      {daySummary && <DaySummaryPanel vehicle={daySummary.vehicle} day={daySummary.date} onClose={() => setDaySummary(null)} onReload={load} />}

      {selectMode && selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl px-5 py-3 flex items-center gap-4" data-testid="run-select-bar">
          <span className="text-sm font-semibold">{selected.length} shift{selected.length === 1 ? "" : "s"} selected</span>
          <button onClick={() => setSelected([])} className="text-xs text-slate-300 hover:text-white">Clear</button>
          <Button onClick={() => setRunOpen(true)} className="h-8 gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full" data-testid="run-create-btn"><Route className="w-4 h-4" /> Create run</Button>
        </div>
      )}
      {runOpen && <RunPlanner shiftIds={selected} onClose={() => setRunOpen(false)} onSaved={() => { setRunOpen(false); setSelectMode(false); setSelected([]); load(); toast.success("Run created"); }} />}
    </div>
  );
}
